import { businessesNearbyQueryOptions, businessesTrendingQueryOptions, cuisinesQueryOptions } from '@/api/queries/businesses';
import { useSetting } from '@/features/settings';
import { Restaurant } from '@/types/display';
import { calculateDistance, formatDistance } from '@/utils/distance';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

interface UseDiscoverDataOptions {
  latitude: number;
  longitude: number;
}

export function useDiscoverData({ latitude, longitude }: UseDiscoverDataOptions) {
  const { value: units } = useSetting('units');

  const { data: nearbyData, isLoading: isNearbyLoading } = useQuery({
    ...businessesNearbyQueryOptions({
      latitude,
      longitude,
      radius_km: 5,
      limit: 20,
    }),
    enabled: latitude !== 0 && longitude !== 0,
  });

  const { data: trendingData, isLoading: isTrendingLoading } = useQuery(
    businessesTrendingQueryOptions({ limit: 10 })
  );

  const { data: cuisinesData, isLoading: isCuisinesLoading } = useQuery(cuisinesQueryOptions());

  const trendingBusinesses = useMemo((): Restaurant[] => {
    if (!trendingData) return [];
    return trendingData.items.map((business) => {
      const distanceText = (latitude !== 0 && longitude !== 0 && business.latitude != null && business.longitude != null)
        ? calculateDistance(latitude, longitude, business.latitude, business.longitude, units)
        : '';
      return {
        id: business.id,
        name: business.name,
        cuisine: business.cuisineType || 'Restaurant',
        rating: business.rating ?? 0,
        reviews: business.totalRatings ?? 0,
        image: business.coverPhotoR2Key ?? '',
        distance: distanceText,
        trending_dish: business.tags && business.tags.length > 0 ? business.tags[0] : undefined,
        ratingDistribution: business.ratingDistribution,
      };
    });
  }, [trendingData, latitude, longitude, units]);

  const nearbyRestaurants = useMemo((): Restaurant[] => {
    if (!nearbyData) return [];
    return nearbyData.items.map((business) => {
      const distanceText = formatDistance(business.distanceKm ?? 0, units);
      return {
        id: business.id,
        name: business.name,
        cuisine: business.cuisineType || 'Restaurant',
        rating: business.rating ?? 0,
        reviews: business.totalRatings ?? 0,
        image: business.coverPhotoR2Key ?? '',
        distance: distanceText,
        trending_dish: business.tags && business.tags.length > 0 ? business.tags[0] : undefined,
        ratingDistribution: business.ratingDistribution,
      };
    });
  }, [nearbyData, units]);

  const cuisines = useMemo((): string[] => {
    if (!cuisinesData) return [];
    return cuisinesData.slice(0, 12);
  }, [cuisinesData]);

  const showEmptyState = !isNearbyLoading && nearbyRestaurants.length === 0
    && !isTrendingLoading && trendingBusinesses.length === 0;

  return {
    trendingBusinesses,
    isTrendingLoading,
    nearbyRestaurants,
    isNearbyLoading,
    cuisines,
    isCuisinesLoading,
    showEmptyState,
  };
}
